import {
  type AlgoClonePayload,
  type ApplicationClientType,
  type LinkerApi,
  type LinkerDataType,
  type MessageRequest,
  type OrderCloneParams,
  type QEPSharePublishData,
  type SingleInputDialogParams,
  type YesNoDialogParams,
} from '..';
import {type AuthenticationType} from './authentication';

// eslint-disable-next-line @typescript-eslint/no-restricted-types -- Browser contracts explicitly distinguish JSON null from omitted fields.
type NullValue = null;

/** Listener accepted by the container's current-window event bridge. */
export type CurrentWindowEventListener = () => unknown;

/** Runtime window facade returned by every supported QEP container. */
export type CurrentWindowType = {
  name: string;
  addEventListener: (event: string, cb?: CurrentWindowEventListener) => void;
  removeEventListener: (event: string, cb?: CurrentWindowEventListener) => void;
};

/** Callback used by Browser container persistence methods, including the legacy null success sentinel. */
type ContainerCallback<T = unknown> = (error: Error | NullValue | undefined, response?: T | NullValue) => unknown;
type PreferenceCallback = (
  error: Error | NullValue | undefined,
  response?: Record<string, unknown> | NullValue,
) => unknown;

export type ContainerType = {
  _init: () => Promise<void>;
  applicationClient: ApplicationClientType | NullValue;
  authentication: AuthenticationType;
  canUseFeature: (feature: string) => boolean;
  isWhitelistedAlgoExchange: (exchange: string) => boolean;
  closeWindow: () => void;
  config: {
    getPreference: () => Promise<{data: Record<string, unknown>}>;
    setPreference: (
      data: {field: string; value: string | number | boolean},
      cb?: PreferenceCallback,
    ) => Promise<Record<string, unknown> | undefined>;
  };
  getCurrentWindow: () => CurrentWindowType;
  getSpawnData: () => AlgoClonePayload | OrderCloneParams | any;
  getSystemInfo: () => Promise<NullValue>;
  getValueFromComponentState: (parameters: {field: string}, cb?: ContainerCallback) => Promise<unknown>;
  getValueFromComponentStateAsync: (parameters: {field: string}) => Promise<unknown>;
  getValueFromStorage: (parameters: {topic: string; key: string}, cb?: ContainerCallback<StorageResponse>) => Promise<StorageResponse | unknown>;
  getValueFromStorageAsync: (parameters: {topic: string; key: string}) => Promise<StorageResponse>;
  getWindowIdentifier: () => string;
  init: () => Promise<void>;
  isReady: boolean;
  linker: LinkerApi;
  name: string;
  onBeforeReady: (cb: () => void) => undefined | void;
  onReady: (cb?: (parameters?: any) => void) => undefined | void;
  openWindow: (publishData: QEPSharePublishData<LinkerDataType>) => Promise<void>;
  saveValueToComponentState: (parameters: {field: string; value: unknown}, cb?: ContainerCallback) => Promise<undefined>;
  saveValueToStorage: (parameters: {topic: string; key: string; value: unknown}, cb?: ContainerCallback) => Promise<unknown>;
  saveValueToStorageAsync: (parameters: {topic: string; key: string; value: unknown}) => Promise<unknown>;
  showWindow: (windowId: string) => Promise<MessageRequest>;
  waitingForBeforeReady: Array<() => void>;
  waitingForReady: Array<() => void>;
  window: {
    /**
     * @deprecated - unused
    */
    getCurrentWindowIdentifier: () => string;
    /**
    * @deprecated - unused
    */
    subscribeToWindowSelection: (cb: () => unknown) => () => void;
    showLoadingScrim: () => void;
    hideLoadingScrim: () => void;
  };
  windowComponentName: string;
  windowIdentifier: string;
  workspace: Record<string, unknown>;

  /**
   * @deprecated - Use `QEP.container.applicationClient` instead
   */
  router: RouterType;
  /**
	 * @deprecated - unused
	 */
  System: {getHostSpecs: () => NullValue};
  /**
   * @deprecated - unused
   */
  createDistributedStore: (_parameters: unknown, cb: ContainerCallback) => void;
  /**
   * @deprecated - unused
   */
  dialog: DialogType;
  /**
   * @deprecated - unused
   */
  query: (topic: string, parameters: unknown, cb: ContainerCallback) => void;
  /**
   * @deprecated - unused
   */
  subscribe: (topic: string, cb: (message: MessageRequest) => void) => void;
};

type DialogType = {
  open: (type: 'yesNo' | 'singleInput', options: YesNoDialogParams | SingleInputDialogParams, handler: (error: Error, response: unknown) => void) => Promise<NullValue>;
  openYesNoDialog: (options: YesNoDialogParams, handler: (error: Error, response: unknown) => void) => Promise<NullValue>;
  openSingleInputDialog: (options: SingleInputDialogParams, handler: (error: Error, response: unknown) => void) => Promise<NullValue>;
};

type RouterType = {
  addListener: (topic: string, cb: ContainerCallback) => undefined | (() => void);
  broadcast: (topic: string, data: unknown) => void;
  removeListener?: (topic: string, cb: ContainerCallback) => void;
  transmit: (topic: string, payload: unknown) => void;
  addResponder: (topic: string, cb: ContainerCallback) => void;
  addPubSubResponder: (topic: string, state: unknown) => void;
  closingComponentListener: (cb: ContainerCallback) => void;
  publish: (topic: string, data: unknown) => void;
  query: (topic: string, parameters: unknown, cb: ContainerCallback) => void;
  spawningComponentListener: (cb: ContainerCallback) => void;
  subscribe: (topic: string, cb: (message: MessageRequest) => void) => void;
};

export type StorageResponse =
  | {status: 'success'; data?: unknown; error?: unknown; src?: 'remote' | 'local'}
  | {status: 'localonly'; data: unknown; error?: unknown; src?: 'remote' | 'local'}
  | {status: 'unsuccessful'; data?: unknown; error: Error | string; src?: 'remote' | 'local'};
